import {
  Checkbox,
  CircularProgress,
  Grid,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  styled,
  Tooltip,
  Typography,
} from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import {
  Add,
  CloudUpload,
  Delete,
  DragIndicator,
  Edit,
  Folder,
  FolderSpecial,
  MoreVert,
  PriorityHigh,
} from "@mui/icons-material";
import axios from "axios";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { fileUploadConfig } from "../../../../_helpers/fileUploadConfig";
import AddDrawingLocation from "./AddDrawingLocation";
import Dropzone from "../../../../components/Dropzone";
import { formatDate, useCanDeleteFolder } from "../../../../_helpers";
import { useTranslation } from "react-i18next";
import { URLS } from "../../../../apis/urls";
import ImagesContainer from "../../../Survey/components/ImagesContainer";
import { useSelector } from "react-redux";

const { webApiUrl } = window.appConfig;

const StyledMenu = styled((props) => (
  <Menu
    elevation={0}
    disableRestoreFocus
    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    transformOrigin={{ vertical: "top", horizontal: "center" }}
    {...props}
  />
))(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: "fit-content",
    color: theme.palette.grey[800],
    boxShadow:
      "rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
    "& .MuiMenu-list": { padding: "4px 0" },
    "& .MuiMenuItem-root": {
      "& .MuiSvgIcon-root": {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      "&:active": { backgroundColor: theme.palette.primary.main },
    },
  },
}));

export default function DrawingLocationsTab(props) {
  const {
    canDrawingDossier,
    canDrop,
    isRootFolder,
    buildings,
    selected,
    selectedDossier,
    currentTab,
    handleUpdateFiles,
    attachmentData,
    handleMoveExistingFiles,
    handleSelectFiles,
    isReadOnly,
    fileUploading,
    handleGetAllImages,
    archivedFiles,
    removedFiles,
    buildingId,
    attachmentType,
    handlePreviewOfFiles,
    markFileAsViewed,
  } = props;

  const classes = useStyles();
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [openAttachmentsMenu, setOpenAttachmentsMenu] = useState({ open: false, Index: "" });
  const [drawingLocationsNames, setDrawingLocationsNames] = useState([]);
  const [fileVisibility, setFileVisibility] = useState({});
  const [triggerAddLevel, setTriggerAddLevel] = useState(false);
  const [triggerLevel2ForParentId, setTriggerLevel2ForParentId] = useState(null);
  const [isDeletingId, setIsDeletingId] = useState(null);

  useEffect(() => {
    if (props.triggerAddLevel) setTriggerAddLevel(true);
  }, [props.triggerAddLevel]);

  const { t } = useTranslation();
  const canDeleteFolder = useCanDeleteFolder(
    openAttachmentsMenu.data && openAttachmentsMenu.data.id,
    props.buildingId
  );
  const fileInputRef = useRef();
  const [isOpenImageContainer, setIsOpenImageContainer] = useState(false);
  const [selectedDrawingLocation, setSelectedDrawingLocation] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFileAdding, setIsFileAdding] = useState(false);
  const [addRecordLoading, setAddRecordLoading] = useState(null);
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const {
    dossier: { isUploading, isMoving },
  } = useSelector((state) => state);

  const fileListToArray = (list) => {
    const arr = [];
    for (let i = 0; i < list.length; i++) arr.push(list.item(i));
    return arr;
  };

  useEffect(() => {
    if (!isMoving && selectedAttachment) {
      setOpenAttachmentsMenu((p) => ({ ...p, open: null, data: null }));
      setSelectedAttachment(null);
    }
  }, [isMoving]);

  useEffect(() => {
    if (!isUploading && isFileAdding) setIsFileAdding(null);
  }, [isFileAdding, isUploading]);

  const openAttachmentsMenuHandler = (event, Index, data, file) => {
    setOpenAttachmentsMenu((p) => ({ ...p, open: event.currentTarget, Index, data }));
    setSelectedAttachment(file);
  };

  useEffect(() => {
    if (selectedDossier) {
      setIsLoading(true);
      const queryParams = new URLSearchParams();
      if (props.buildingId) queryParams.set("buildingId", props.buildingId);
      const query = queryParams.toString() ? `?${queryParams.toString()}` : "";
      const url = `${webApiUrl}api/Dossier/GetDrawingLocationNamesForProject/${selectedDossier.projectId}${query}`;
      axios
        .get(url)
        .then((res) => {
          setDrawingLocationsNames(res.data);
          setIsLoading(false);
        })
        .catch(() => setIsLoading(false));
    }
  }, [props.buildingId]);

  // --- Tree helpers ---

  const updateInTree = (tree, updatedItem) =>
    tree.map((item) => {
      if (item.id === updatedItem.id) return { ...item, ...updatedItem };
      if (item.children) return { ...item, children: updateInTree(item.children, updatedItem) };
      return item;
    });

  const removeFromTree = (tree, id) =>
    tree
      .filter((item) => item.id !== id)
      .map((item) =>
        item.children ? { ...item, children: removeFromTree(item.children, id) } : item
      );

  const addToTree = (tree, newItem) => {
    if (!newItem.parentId) return [...tree, newItem];
    return tree.map((item) => {
      if (item.id === newItem.parentId)
        return { ...item, children: [...(item.children || []), newItem] };
      if (item.children) return { ...item, children: addToTree(item.children, newItem) };
      return item;
    });
  };

  const findInTree = (tree, id) => {
    for (const item of tree) {
      if (item.id === id) return item;
      if (item.children) {
        const found = findInTree(item.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  // --- Data handlers ---

  const recordSaveHandler = (data) => {
    const sendId = selectedRecord ? { id: selectedRecord.id } : {};
    // When renaming an existing item, preserve its current parent
    const parentId = selectedRecord ? (selectedRecord.parentId || null) : (data.parentId || null);
    setAddRecordLoading(selectedRecord?.id || 'creating');
    axios
      .post(`${webApiUrl}api/Dossier/AddOrUpdateDrawingLocationNameForProject`, {
        name: data.inputValue || data.name,
        projectId: selectedDossier.projectId,
        buildingId: props.buildingId,
        parentId,
        isLevel: !!data.isLevel,
        ...sendId,
      })
      .then(({ data: { viewModel } }) => {
        setAddRecordLoading(null);
        setDrawingLocationsNames((prev) => {
          if (selectedRecord) return updateInTree(prev, viewModel);
          return addToTree(prev, viewModel);
        });
      })
      .catch(() => setAddRecordLoading(null));
  };

  const deleteDrawingLocationHandler = () => {
    if (!canDeleteFolder) {
      handleUpdateFiles({ isDeleted: true }, false, selectedAttachment);
      setSelectedAttachment(null);
    } else {
      const deletingId = openAttachmentsMenu.data.id;
      setIsDeletingId(deletingId);
      axios
        .delete(
          `${webApiUrl}api/Dossier/DeleteDrawingLocationNameForProject/${deletingId}`
        )
        .then(() => {
          setIsDeletingId(null);
          setDrawingLocationsNames((prev) => removeFromTree(prev, deletingId));
          setOpenAttachmentsMenu((p) => ({ ...p, open: null, data: null }));
          setSelectedAttachment(null);
        })
        .catch(() => setIsDeletingId(null));
    }
  };

  const handleItemClick = (item) => {
    if (currentTab === 1 || currentTab === 2) {
      setIsOpenImageContainer((p) => !p);
      setSelectedDrawingLocation(item);
    }
  };

  const handleSetFileVisibility = (fileId, isVisibleToBuyer) => {
    axios
      .patch(`${webApiUrl}api/Dossier/SetFileVisibility/${fileId}?isVisibleToBuyer=${isVisibleToBuyer}`)
      .catch(console.log);
    setFileVisibility((p) => ({ ...p, [fileId]: !isVisibleToBuyer }));
  };

  const getIsInternal = (attachment) => {
    if (fileVisibility[attachment.fileId] !== undefined) return fileVisibility[attachment.fileId];
    return attachment.isInternal !== undefined ? attachment.isInternal : false;
  };

  // --- DnD helpers ---

  const buildFullPayload = (state) => {
    const payload = [];
    const level1Items = state.filter((x) => x.isLevel);
    const rootContainers = state.filter((x) => !x.isLevel);
    payload.push({ parentId: null, orderedIds: level1Items.map((x) => x.id) });
    payload.push({ parentId: null, orderedIds: rootContainers.map((x) => x.id) });
    level1Items.forEach((level1) => {
      const level2s = (level1.children || []).filter((c) => c.isLevel);
      const directContainers = (level1.children || []).filter((c) => !c.isLevel);
      payload.push({
        parentId: level1.id,
        orderedIds: [...level2s.map((x) => x.id), ...directContainers.map((x) => x.id)],
      });
      level2s.forEach((level2) => {
        payload.push({
          parentId: level2.id,
          orderedIds: (level2.children || []).filter((c) => !c.isLevel).map((x) => x.id),
        });
      });
    });
    return payload;
  };

  const onDragEnd = ({ source, destination, draggableId }) => {
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    let newState = JSON.parse(JSON.stringify(drawingLocationsNames));

    // Level 1 reordering
    if (source.droppableId === "levels") {
      if (destination.droppableId !== "levels") return;
      const level1s = newState.filter((x) => x.isLevel);
      const [moved] = level1s.splice(source.index, 1);
      level1s.splice(destination.index, 0, moved);
      newState = [...level1s, ...newState.filter((x) => !x.isLevel)];
      setDrawingLocationsNames(newState);
      axios.post(`${webApiUrl}api/Dossier/SaveDrawingLocationsOrder`, buildFullPayload(newState));
      return;
    }

    // Level 2 reordering within / between Level 1s
    if (source.droppableId.startsWith("sublevel-")) {
      if (!destination.droppableId.startsWith("sublevel-")) return;
      const srcLevel1Id = source.droppableId.replace("sublevel-", "");
      const dstLevel1Id = destination.droppableId.replace("sublevel-", "");
      const srcLevel1 = newState.find((x) => x.id === srcLevel1Id);
      if (!srcLevel1) return;
      const srcLevel2s = (srcLevel1.children || []).filter((c) => c.isLevel);
      const [movedLevel2] = srcLevel2s.splice(source.index, 1);
      if (srcLevel1Id === dstLevel1Id) {
        srcLevel2s.splice(destination.index, 0, movedLevel2);
        srcLevel1.children = [...srcLevel2s, ...(srcLevel1.children || []).filter((c) => !c.isLevel)];
      } else {
        movedLevel2.parentId = dstLevel1Id;
        srcLevel1.children = [...srcLevel2s, ...(srcLevel1.children || []).filter((c) => !c.isLevel)];
        const dstLevel1 = newState.find((x) => x.id === dstLevel1Id);
        if (!dstLevel1) return;
        const dstLevel2s = (dstLevel1.children || []).filter((c) => c.isLevel);
        dstLevel2s.splice(destination.index, 0, movedLevel2);
        dstLevel1.children = [...dstLevel2s, ...(dstLevel1.children || []).filter((c) => !c.isLevel)];
      }
      setDrawingLocationsNames(newState);
      axios.post(`${webApiUrl}api/Dossier/SaveDrawingLocationsOrder`, buildFullPayload(newState));
      return;
    }

    // Container DnD (within level2 groups or root)
    const srcParentId =
      source.droppableId === "root" ? null : source.droppableId.replace("group-", "");
    const dstParentId =
      destination.droppableId === "root" ? null : destination.droppableId.replace("group-", "");

    let draggedItem = null;

    if (srcParentId === null) {
      const rootItems = newState.filter((x) => !x.isLevel);
      draggedItem = rootItems[source.index];
      newState = newState.filter((x) => x.id !== draggableId);
    } else {
      const srcParent = findInTree(newState, srcParentId);
      if (srcParent) {
        const srcContainers = (srcParent.children || []).filter((c) => !c.isLevel);
        draggedItem = srcContainers[source.index];
        srcParent.children = (srcParent.children || []).filter((c) => c.id !== draggableId);
      }
    }

    if (!draggedItem) return;
    draggedItem.parentId = dstParentId;

    if (dstParentId === null) {
      const level1s = newState.filter((x) => x.isLevel);
      const roots = newState.filter((x) => !x.isLevel);
      roots.splice(destination.index, 0, draggedItem);
      newState = [...level1s, ...roots];
    } else {
      const dstParent = findInTree(newState, dstParentId);
      if (dstParent) {
        const dstLevel2s = (dstParent.children || []).filter((c) => c.isLevel);
        const dstContainers = (dstParent.children || []).filter((c) => !c.isLevel);
        dstContainers.splice(destination.index, 0, draggedItem);
        dstParent.children = [...dstLevel2s, ...dstContainers];
      }
    }

    setDrawingLocationsNames(newState);
    axios.post(`${webApiUrl}api/Dossier/SaveDrawingLocationsOrder`, buildFullPayload(newState));
  };

  // --- Folder card renderer ---

  const renderFolderCard = (folder, index, dragHandleProps) => {
    const attachment =
      attachmentData && !!attachmentData.length
        ? attachmentData.find((a) => a.drawingLocationId === folder.id)
        : null;

    const isInternal = attachment ? getIsInternal(attachment) : false;

    return (
      <Grid
        className={classes.floorThumnailContainer}
        container
        sx={{ justifyContent: "center" }}
      >
        <div
          onClick={() => handleItemClick(folder)}
          className={clsx(classes.dropzone, classes.folderCustomDropStyle)}
        >
          {currentTab === 0 && isRootFolder && (
            <div className={classes.topActions}>
              {dragHandleProps && (
                <span
                  {...dragHandleProps}
                  className={classes.dragHandle}
                  onClick={(e) => e.stopPropagation()}
                >
                  <DragIndicator fontSize="small" />
                </span>
              )}
              <IconButton
                disabled={isUploading && isFileAdding === folder.id}
                className={classes.moreIconStyle}
                onClick={(e) => {
                  e.stopPropagation();
                  openAttachmentsMenuHandler(e, index, folder, attachment);
                }}
                size="large"
              >
                <MoreVert />
              </IconButton>
            </div>
          )}
          {attachment ? (
            <Grid
              onClick={() => {
                currentTab === 0 && handleGetAllImages("preview", [attachment]);
              }}
            >
              {isUploading && isFileAdding === folder.id ? (
                <CircularProgress size={20} />
              ) : (
                <Grid
                  onClick={() => {
                    if (currentTab === 0 && attachment.hasUpdates)
                      markFileAsViewed(
                        attachment.fileId,
                        buildingId,
                        null,
                        attachmentType,
                        attachment.isGeneralFile
                      );
                  }}
                  onMouseEnter={() => {
                    if (currentTab === 0 && attachment.hasUpdates)
                      markFileAsViewed(
                        attachment.fileId,
                        buildingId,
                        null,
                        attachmentType,
                        attachment.isGeneralFile
                      );
                  }}
                  className={classes.thumnailContainer}
                  container
                  sx={{ justifyContent: "center" }}
                  size={{ xs: 12 }}
                >
                  {((currentTab !== 0
                    ? (currentTab === 1 ? archivedFiles : removedFiles)
                        .filter((file) => file.drawingLocationId === folder.id)
                        .some((a) => a.hasUpdates)
                    : attachment.hasUpdates)) && (
                    <Tooltip title={t("dossier.sidebar.priority.high")}>
                      <PriorityHigh
                        color="error"
                        style={{ position: "absolute", left: -4, top: 3 }}
                      />
                    </Tooltip>
                  )}
                  <div
                    className={classes.thumbnail}
                    style={{
                      backgroundImage: `url("${URLS.GET_UPLOADED_DOSSIER_FILE_THUMBNAIL}${attachment.fileId}")`,
                    }}
                  />
                  <Typography variant="caption" className={classes.caption} noWrap>
                    {attachment.name}
                  </Typography>
                  <Typography variant="caption" className={classes.caption} noWrap>
                    {formatDate(new Date(attachment.lastModifiedOn))}
                  </Typography>
                </Grid>
              )}
            </Grid>
          ) : (
            isRootFolder && (
              <Grid style={{ margin: "0 auto" }}>
                <Grid
                  className={classes.thumnailContainer}
                  container
                  sx={{ justifyContent: "center" }}
                >
                  {isUploading && isFileAdding === folder.id ? (
                    <CircularProgress size={20} />
                  ) : (
                    <Dropzone
                      className={classes.dropzone}
                      canDrawingDossier={canDrawingDossier}
                      handlePreviewOfFiles={handlePreviewOfFiles}
                      accept={
                        canDrawingDossier
                          ? "application/pdf"
                          : fileUploadConfig.allowedMimeTypes.map((f) => f.mime).join()
                      }
                      selectedObjects={selectedDossier.buildingInfoList}
                      buildings={buildings.filter((x) => x.projectId === selected.projectId)}
                      withUploadFileDialog={!!buildings.length}
                      handleSelectExistingImages={(data) =>
                        handleMoveExistingFiles(data, folder.id)
                      }
                      onFilesAdded={(data) => {
                        handleSelectFiles(data, { isActive: true, drawingLocationId: folder.id });
                        setIsFileAdding(folder.id);
                      }}
                      disabled={isReadOnly || canDrop}
                      uploading={fileUploading}
                    />
                  )}
                </Grid>
              </Grid>
            )
          )}
        </div>
        <Tooltip title={folder.name}>
          <Typography variant="h6" className={classes.folderTitle}>
            {addRecordLoading === folder.id ? <CircularProgress size={18} /> : folder.name}
          </Typography>
        </Tooltip>
        {currentTab === 0 && isRootFolder && !props.isBuyer && attachment && (
          <div className={classes.visibilityRow} onClick={(e) => e.stopPropagation()}>
            <Checkbox
              size="small"
              checked={!isInternal}
              onChange={(e) => {
                e.stopPropagation();
                handleSetFileVisibility(attachment.fileId, e.target.checked);
              }}
            />
            <Typography variant="caption" className={classes.visibilityLabel}>
              {t("dossier.visibleToBuyer")}
            </Typography>
          </div>
        )}
      </Grid>
    );
  };

  const level1Items = drawingLocationsNames.filter((x) => x.isLevel);
  const rootContainers = drawingLocationsNames.filter((x) => !x.isLevel);

  return isLoading ? (
    <div className={classes.loaderContainer}>
      <CircularProgress size={20} />
    </div>
  ) : (
    <>
      <Grid className={classes.floorFilesContainer} size={{ xs: 12 }}>
        {(addRecordLoading !== null || isDeletingId !== null) && (
          <LinearProgress style={{ marginBottom: 4 }} />
        )}
        <input
          ref={fileInputRef}
          className={classes.fileInput}
          type="file"
          accept={"application/pdf"}
          onChange={({ target: { files } }) => {
            setIsFileAdding(openAttachmentsMenu.data.id);
            handleSelectFiles(fileListToArray(files), {
              isActive: true,
              drawingLocationId: openAttachmentsMenu.data.id,
            });
            setOpenAttachmentsMenu((p) => ({ ...p, open: null }));
          }}
        />
        <StyledMenu
          anchorEl={openAttachmentsMenu.open}
          keepMounted
          open={Boolean(openAttachmentsMenu.open)}
          onClose={() => setOpenAttachmentsMenu((p) => ({ ...p, open: null }))}
        >
          {(() => {
            const _menuData = openAttachmentsMenu.data;
            const _levelHasChildren = !!_menuData?.isLevel && (_menuData.children || []).length > 0;
            const _containerHasNonActiveFiles = !_menuData?.isLevel && !!_menuData &&
              ((archivedFiles || []).some(f => f.drawingLocationId === _menuData.id) ||
               (removedFiles || []).some(f => f.drawingLocationId === _menuData.id));
            const _deleteBlocked = _levelHasChildren || _containerHasNonActiveFiles;
            if (!_deleteBlocked && !selectedAttachment && !canDeleteFolder && !isMoving && !isDeletingId) return null;
            return (
              <MenuItem
                disabled={_deleteBlocked || isMoving || !!isDeletingId || (!selectedAttachment && !canDeleteFolder)}
                onClick={() => deleteDrawingLocationHandler()}
              >
                {(isMoving || isDeletingId === _menuData?.id) ? <CircularProgress size={20} /> : <Delete />}
                {t("general.delete")}
              </MenuItem>
            );
          })()}
          {!openAttachmentsMenu.data?.isLevel && (
            <MenuItem onClick={() => fileInputRef.current.click()}>
              <CloudUpload />
              {t("dossier.general.uploadImageButton.title")}
            </MenuItem>
          )}
          {/* "Niveau toevoegen" only on Level 1 items → creates a Level 2 sub-section */}
          {isRootFolder && currentTab === 0 &&
            openAttachmentsMenu.data?.isLevel && !openAttachmentsMenu.data?.parentId && (
            <MenuItem
              onClick={() => {
                setTriggerLevel2ForParentId(openAttachmentsMenu.data.id);
                setOpenAttachmentsMenu((p) => ({ ...p, open: null }));
              }}
            >
              <Add />
              {t("drawing.dossier.create.level.button")}
            </MenuItem>
          )}
          <MenuItem
            onClick={() => {
              setSelectedRecord(openAttachmentsMenu.data);
              setOpenAttachmentsMenu((p) => ({ ...p, open: null }));
            }}
          >
            <Edit />
            {t("drawing.dossier.rename.title")}
          </MenuItem>
        </StyledMenu>

        <DragDropContext onDragEnd={isRootFolder && currentTab === 0 ? onDragEnd : () => {}}>

          {/* ── Root-level containers (standalone, no group) ── */}
          <Droppable droppableId="root" direction="horizontal">
            {(provided) => (
              <Grid
                container
                className={classes.floorGridContainer}
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                {rootContainers.map((folder, i) => {
                  const hasFile =
                    attachmentData && attachmentData.some((a) => a.drawingLocationId === folder.id);
                  if (!isRootFolder && !hasFile) return null;
                  return (
                    <Draggable
                      key={folder.id}
                      draggableId={folder.id}
                      index={i}
                      isDragDisabled={!isRootFolder || currentTab !== 0}
                    >
                      {(provided) => (
                        <div ref={provided.innerRef} {...provided.draggableProps}>
                          {renderFolderCard(folder, i, provided.dragHandleProps)}
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
                {isRootFolder && currentTab === 0 && (
                  <Grid style={{ margin: "0 auto" }}>
                    <AddDrawingLocation
                      existingFloors={rootContainers}
                      buildingId={props.buildingId}
                      state={selectedRecord && !selectedRecord.isLevel ? selectedRecord : null}
                      canOpen={!!(selectedRecord && !selectedRecord.isLevel)}
                      onSave={(data) => {
                        if (data) recordSaveHandler(data);
                        setSelectedRecord(null);
                      }}
                    />
                  </Grid>
                )}
              </Grid>
            )}
          </Droppable>

          {/* ── Level 1 sections ── */}
          <Droppable droppableId="levels" direction="vertical" type="LEVEL1">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {level1Items.map((level1, li) => (
                  <Draggable
                    key={level1.id}
                    draggableId={level1.id}
                    index={li}
                    isDragDisabled={!isRootFolder || currentTab !== 0}
                  >
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={classes.levelSection}
                      >
                        {/* Level 1 header */}
                        <div className={classes.levelHeader}>
                          {isRootFolder && currentTab === 0 && (
                            <span {...provided.dragHandleProps} className={classes.dragHandle}>
                              <DragIndicator fontSize="small" />
                            </span>
                          )}
                          <FolderSpecial className={classes.levelIcon} />
                          <Typography className={classes.levelTitle}>{level1.name}</Typography>
                          {isRootFolder && currentTab === 0 && (
                            <IconButton
                              size="small"
                              onClick={(e) => openAttachmentsMenuHandler(e, li, level1, null)}
                            >
                              <MoreVert />
                            </IconButton>
                          )}
                        </div>

                        {/* ── Containers directly in Level 1 (shown first / "main") ── */}
                        <Droppable droppableId={`group-${level1.id}`} direction="horizontal">
                          {(provided) => (
                            <Grid
                              container
                              className={classes.floorGridContainer}
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                            >
                              {(level1.children || []).filter((c) => !c.isLevel).map((child, ci) => {
                                const hasFile =
                                  attachmentData &&
                                  attachmentData.some((a) => a.drawingLocationId === child.id);
                                if (!isRootFolder && !hasFile) return null;
                                return (
                                  <Draggable
                                    key={child.id}
                                    draggableId={child.id}
                                    index={ci}
                                    isDragDisabled={!isRootFolder || currentTab !== 0}
                                  >
                                    {(provided) => (
                                      <div ref={provided.innerRef} {...provided.draggableProps}>
                                        {renderFolderCard(child, ci, provided.dragHandleProps)}
                                      </div>
                                    )}
                                  </Draggable>
                                );
                              })}
                              {provided.placeholder}
                              {isRootFolder && currentTab === 0 && (
                                <Grid style={{ margin: "0 auto" }}>
                                  <AddDrawingLocation
                                    parentId={level1.id}
                                    existingFloors={(level1.children || []).filter((c) => !c.isLevel)}
                                    buildingId={props.buildingId}
                                    onSave={(data) => {
                                      if (data) recordSaveHandler(data);
                                    }}
                                  />
                                </Grid>
                              )}
                            </Grid>
                          )}
                        </Droppable>

                        {/* ── Level 2 sub-sections (shown after / "sub") ── */}
                        <Droppable droppableId={`sublevel-${level1.id}`} direction="vertical" type="LEVEL2">
                          {(provided) => (
                            <div ref={provided.innerRef} {...provided.droppableProps}>
                              {(level1.children || []).filter((c) => c.isLevel).map((level2, j) => {
                                const level2HasContent =
                                  isRootFolder ||
                                  (level2.children || []).some(
                                    (c) => attachmentData && attachmentData.some((a) => a.drawingLocationId === c.id)
                                  );
                                if (!level2HasContent) return null;
                                return (
                                  <Draggable
                                    key={level2.id}
                                    draggableId={level2.id}
                                    index={j}
                                    isDragDisabled={!isRootFolder || currentTab !== 0}
                                  >
                                    {(provided) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        className={classes.level2Section}
                                      >
                                        {/* Level 2 header */}
                                        <div className={classes.level2Header}>
                                          {isRootFolder && currentTab === 0 && (
                                            <span {...provided.dragHandleProps} className={classes.dragHandle}>
                                              <DragIndicator style={{ fontSize: 16 }} />
                                            </span>
                                          )}
                                          <Folder className={classes.level2Icon} />
                                          <Typography className={classes.level2Title}>{level2.name}</Typography>
                                          {isRootFolder && currentTab === 0 && (
                                            <IconButton
                                              size="small"
                                              onClick={(e) => openAttachmentsMenuHandler(e, j, level2, null)}
                                            >
                                              <MoreVert />
                                            </IconButton>
                                          )}
                                        </div>

                                        {/* Containers inside Level 2 */}
                                        <Droppable droppableId={`group-${level2.id}`} direction="horizontal">
                                          {(provided) => (
                                            <Grid
                                              container
                                              className={classes.floorGridContainer}
                                              ref={provided.innerRef}
                                              {...provided.droppableProps}
                                            >
                                              {(level2.children || []).filter((c) => !c.isLevel).map((child, ci) => {
                                                const hasFile =
                                                  attachmentData &&
                                                  attachmentData.some((a) => a.drawingLocationId === child.id);
                                                if (!isRootFolder && !hasFile) return null;
                                                return (
                                                  <Draggable
                                                    key={child.id}
                                                    draggableId={child.id}
                                                    index={ci}
                                                    isDragDisabled={!isRootFolder || currentTab !== 0}
                                                  >
                                                    {(provided) => (
                                                      <div ref={provided.innerRef} {...provided.draggableProps}>
                                                        {renderFolderCard(child, ci, provided.dragHandleProps)}
                                                      </div>
                                                    )}
                                                  </Draggable>
                                                );
                                              })}
                                              {provided.placeholder}
                                              {isRootFolder && currentTab === 0 && (
                                                <Grid style={{ margin: "0 auto" }}>
                                                  <AddDrawingLocation
                                                    parentId={level2.id}
                                                    existingFloors={(level2.children || []).filter((c) => !c.isLevel)}
                                                    buildingId={props.buildingId}
                                                    onSave={(data) => {
                                                      if (data) recordSaveHandler(data);
                                                    }}
                                                  />
                                                </Grid>
                                              )}
                                            </Grid>
                                          )}
                                        </Droppable>
                                      </div>
                                    )}
                                  </Draggable>
                                );
                              })}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>

                        {/* Level 2 dialog — button hidden, triggered via Level 1's 3-dots or rename */}
                        <div style={{ display: 'none' }}>
                          <AddDrawingLocation
                            isLevel
                            parentId={level1.id}
                            existingFloors={(level1.children || []).filter((c) => c.isLevel)}
                            buildingId={props.buildingId}
                            canOpen={
                              triggerLevel2ForParentId === level1.id ||
                              !!(selectedRecord?.isLevel && selectedRecord?.parentId === level1.id)
                            }
                            state={
                              selectedRecord?.isLevel && selectedRecord?.parentId === level1.id
                                ? selectedRecord
                                : null
                            }
                            onSave={(data) => {
                              if (data) recordSaveHandler(data);
                              setTriggerLevel2ForParentId(null);
                              if (selectedRecord?.parentId === level1.id) setSelectedRecord(null);
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>

        </DragDropContext>

        {/* Level 1 dialog — button hidden, opened via global ⋮ or rename */}
        <div style={{ display: 'none' }}>
          <AddDrawingLocation
            isLevel
            existingFloors={level1Items}
            buildingId={props.buildingId}
            state={selectedRecord?.isLevel && !selectedRecord?.parentId ? selectedRecord : null}
            canOpen={!!(selectedRecord?.isLevel && !selectedRecord?.parentId) || triggerAddLevel}
            onSave={(data) => {
              if (data) recordSaveHandler(data);
              setSelectedRecord(null);
              setTriggerAddLevel(false);
              props.onAddLevelDone?.();
            }}
          />
        </div>
      </Grid>

      {isOpenImageContainer && selectedDrawingLocation && (
        <ImagesContainer
          hideTitle
          onClose={() => {
            setIsOpenImageContainer(false);
            setSelectedDrawingLocation(null);
          }}
          handleView={(attachment) =>
            markFileAsViewed(
              attachment.fileId,
              buildingId,
              null,
              attachmentType,
              attachment.isGeneralFile
            )
          }
          showFileTitle
          onPreview={() =>
            handleGetAllImages(
              "preview",
              (currentTab === 1 ? archivedFiles : removedFiles).filter(
                (file) => file.drawingLocationId === selectedDrawingLocation.id
              )
            )
          }
          hideCheckBox
          open={isOpenImageContainer}
          data={(currentTab === 1 ? archivedFiles : removedFiles).filter(
            (file) => file.drawingLocationId === selectedDrawingLocation.id
          )}
          drawingPDFThumbnail
        />
      )}
    </>
  );
}

const useStyles = makeStyles((theme) => ({
  fileInput: { display: "none" },
  loaderContainer: {
    marginTop: theme.spacing(2),
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  floorFilesContainer: {
    maxHeight: 600,
    minHeight: 177,
    overflow: "auto",
    padding: theme.spacing(1.25, 1.25, 3.25, 1.25),
  },
  // Level 1 section
  levelSection: {
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: 6,
    padding: theme.spacing(1, 1, 2, 1),
    background: theme.palette.grey[50],
  },
  levelHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
  levelIcon: {
    color: theme.palette.primary.main,
    marginRight: theme.spacing(1),
    fontSize: 20,
  },
  levelTitle: {
    flexGrow: 1,
    fontWeight: 600,
    fontSize: 14,
    color: theme.palette.grey[800],
  },
  // Level 2 section (inside Level 1)
  level2Section: {
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.grey[100]}`,
    borderRadius: 4,
    padding: theme.spacing(0.75),
    background: theme.palette.common.white,
    marginLeft: theme.spacing(1),
  },
  level2Header: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(0.5),
  },
  level2Icon: {
    color: theme.palette.primary.light,
    marginRight: theme.spacing(0.75),
    fontSize: 16,
  },
  level2Title: {
    flexGrow: 1,
    fontWeight: 500,
    fontSize: 13,
    color: theme.palette.grey[700],
  },
  topActions: {
    position: "absolute",
    top: 0,
    right: 0,
    display: "flex",
    alignItems: "center",
    zIndex: 1,
  },
  dragHandle: {
    cursor: "grab",
    display: "flex",
    alignItems: "center",
    color: theme.palette.grey[400],
    "&:hover": { color: theme.palette.grey[700] },
  },
  thumbnail: {
    backgroundPosition: "center",
    backgroundSize: "contain",
    backgroundRepeat: "no-repeat",
    height: 80,
    width: "100%",
    display: "block",
  },
  caption: {
    width: "100%",
    textAlign: "center",
    [theme.breakpoints.down("sm")]: { fontSize: 10 },
  },
  thumnailContainer: {
    width: 135,
    height: 135,
    cursor: "pointer",
    minHeight: 120,
    display: "flex",
    position: "relative",
    alignItems: "center",
    flexDirection: "column",
    justifyContent: "space-evenly",
  },
  floorThumnailContainer: {
    width: 135,
    minHeight: 120,
    cursor: "pointer",
    display: "flex",
    position: "relative",
    alignItems: "center",
    flexDirection: "column",
    justifyContent: "flex-start",
  },
  floorGridContainer: {
    justifyContent: "space-around",
    gridTemplateColumns: "repeat(auto-fill, 135px)",
    display: "grid",
    gap: 10,
    rowGap: 20,
    paddingBottom: theme.spacing(1.5),
  },
  visibilityRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginTop: -2,
    cursor: "default",
  },
  visibilityLabel: {
    fontSize: 10,
    color: theme.palette.grey[600],
    whiteSpace: "nowrap",
  },
  dropzone: {
    padding: 0,
    height: "100%",
  },
  folderCustomDropStyle: {
    width: "100%",
    minHeight: 120,
    position: "relative",
    display: "flex",
    fontSize: "16px",
    alignItems: "center",
    borderRadius: "5px",
    flexDirection: "column",
    justifyContent: "center",
    backgroundColor: theme.palette.common.white,
  },
  moreIconStyle: {
    padding: 2,
  },
  folderTitle: {
    fontSize: 14,
    fontWeight: "bold",
    color: theme.palette.grey[800],
    marginTop: 10,
    display: "-webkit-box",
    maxWidth: 135,
    "-webkit-line-clamp": 1,
    "-webkit-box-orient": "vertical",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
}));
